import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, Trash2 } from 'lucide-react';
import { useAccounts } from '../hooks/useAccounts';
import {
  listAdgroupsForAccount,
  listZohoForms,
  upsertMapping,
  deleteMapping,
  type TiktokAdgroupWithMapping,
} from '../api/tiktokMappings';

/**
 * TikTok adgroup → Zoho form-name mapping admin page.
 * One row per adgroup; user picks the matching Zoho form, hits Save.
 * Once mapped, every ad in that adgroup is renamed (and re-attributed) to
 * the chosen form across the dashboard.
 */
export default function TiktokMappings() {
  const { t } = useTranslation();
  const { data: accounts } = useAccounts();
  const queryClient = useQueryClient();

  const tiktokAccounts = useMemo(
    () => (accounts ?? []).filter((a) => a.platform === 'tiktok'),
    [accounts]
  );

  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  useEffect(() => {
    if (!selectedAccountId && tiktokAccounts[0]) {
      setSelectedAccountId(tiktokAccounts[0]._id);
    }
  }, [tiktokAccounts, selectedAccountId]);

  const adgroupsQuery = useQuery({
    queryKey: ['tiktok-mappings:adgroups', selectedAccountId],
    queryFn: () => listAdgroupsForAccount(selectedAccountId),
    enabled: !!selectedAccountId,
    staleTime: 5 * 60 * 1000,
  });
  const formsQuery = useQuery({
    queryKey: ['tiktok-mappings:forms'],
    queryFn: listZohoForms,
    staleTime: 10 * 60 * 1000,
  });

  // Local draft state — each adgroup's pending selection before Save.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const upsert = useMutation({
    mutationFn: upsertMapping,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiktok-mappings:adgroups', selectedAccountId] });
      // Insights data is downstream of mappings — force its refetch too.
      queryClient.invalidateQueries({ queryKey: ['insights'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-snapshot'] });
    },
  });
  const clear = useMutation({
    mutationFn: ({ accountId, adgroupId }: { accountId: string; adgroupId: string }) =>
      deleteMapping(accountId, adgroupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiktok-mappings:adgroups', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['insights'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-snapshot'] });
    },
  });

  const handleSave = async (ag: TiktokAdgroupWithMapping) => {
    const formName = (drafts[ag.adgroupId] ?? ag.mappedFormName ?? '').trim();
    if (!formName) return;
    await upsert.mutateAsync({
      accountId: selectedAccountId,
      adgroupId: ag.adgroupId,
      adgroupName: ag.adgroupName,
      formName,
    });
    setDrafts((d) => {
      const copy = { ...d };
      delete copy[ag.adgroupId];
      return copy;
    });
  };
  const handleClear = async (ag: TiktokAdgroupWithMapping) => {
    await clear.mutateAsync({ accountId: selectedAccountId, adgroupId: ag.adgroupId });
    setDrafts((d) => {
      const copy = { ...d };
      delete copy[ag.adgroupId];
      return copy;
    });
  };

  if (!tiktokAccounts.length) {
    return (
      <div className="rounded-md border border-gray-200 bg-white p-6">
        <p className="text-sm text-gray-600">{t('tiktokMappings.noTiktokAccount')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('tiktokMappings.title')}
        </h1>
        <p className="mt-1 text-sm text-gray-600">{t('tiktokMappings.subtitle')}</p>
      </div>

      {tiktokAccounts.length > 1 && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">
            {t('tiktokMappings.accountLabel')}
          </label>
          <select
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            {tiktokAccounts.map((a) => (
              <option key={a._id} value={a._id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {adgroupsQuery.isLoading || formsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('common.loading')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-start text-xs font-semibold text-gray-700">
                  {t('tiktokMappings.adgroup')}
                </th>
                <th className="px-4 py-2 text-start text-xs font-semibold text-gray-700">
                  {t('tiktokMappings.form')}
                </th>
                <th className="px-4 py-2 text-end text-xs font-semibold text-gray-700">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(adgroupsQuery.data ?? []).map((ag) => {
                const draft = drafts[ag.adgroupId];
                const current = draft ?? ag.mappedFormName ?? '';
                const dirty = draft !== undefined && draft !== (ag.mappedFormName ?? '');
                return (
                  <tr key={ag.adgroupId}>
                    <td className="px-4 py-2 text-sm text-gray-900">
                      <div>{ag.adgroupName}</div>
                      <div className="text-xs text-gray-500">{ag.adgroupId}</div>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <select
                        value={current}
                        onChange={(e) =>
                          setDrafts((d) => ({ ...d, [ag.adgroupId]: e.target.value }))
                        }
                        className="min-w-[260px] rounded-md border border-gray-300 px-2 py-1 text-sm"
                      >
                        <option value="">— {t('tiktokMappings.unmapped')} —</option>
                        {(formsQuery.data ?? []).map((f) => (
                          <option key={f.formName} value={f.formName}>
                            {f.formName} ({f.count})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 text-end text-sm">
                      <button
                        onClick={() => handleSave(ag)}
                        disabled={!dirty || !current || upsert.isPending}
                        className="me-2 inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-40"
                      >
                        <Save className="h-3.5 w-3.5" />
                        {t('common.save')}
                      </button>
                      {ag.mappedFormName && (
                        <button
                          onClick={() => handleClear(ag)}
                          disabled={clear.isPending}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t('common.delete')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!adgroupsQuery.data?.length && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500">
                    {t('tiktokMappings.noAdgroups')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
